import $dayjs from '@/services/date'

/**
 * Determines whether the month component of a date to be presented should
 * proceed the day component for a specified locale.
 *
 * @param locale The locale to check
 * @returns {Boolean}
 */
const isMonthBeforeDay = (locale) => {
  return (
    $dayjs()
      .locale(locale.toLowerCase())
      .localeData()
      .longDateFormat('L') === 'MM/DD/YYYY'
  )
}

/**
 * Helps with sorting Dates by providing a difference in time between
 * two Dates expressed in milliseconds.
 *
 * @param {Date} firstDate
 * @param {Date} secondDate
 * @returns {Number}
 */
const sortDate = (firstDate, secondDate) =>
  $dayjs(firstDate).diff($dayjs(secondDate))

/**
 * Returns a ISO date string that represents today's date
 * @returns {string}
 */
const getTodaysDateString = () => {
  return $dayjs().toISOString()
}

/**
 * Returns today's date as a YYYY-MM-DD string in the local timezone.
 * Unlike getTodaysDateString which returns UTC via toISOString(),
 * this avoids the date being a day behind in UTC+ timezones.
 * @returns {string}
 */
const getLocalDateString = () => {
  return $dayjs().format('YYYY-MM-DD')
}

/**
 * Converts a YYYY-MM-DD date string to the start of that day in local time.
 * @param {string} dateString
 * @returns {string} ISO-ish datetime e.g. '2026-03-19T00:00:00'
 */
const toStartOfDay = (dateString) => {
  return $dayjs(dateString)
    .startOf('day')
    .format('YYYY-MM-DDTHH:mm:ss')
}

/**
 * Returns the current local time if the given date is today,
 * otherwise returns the end of that day (23:59:59).
 * @param {string} dateString - YYYY-MM-DD
 * @returns {string} e.g. '2026-03-19T14:35:22' or '2026-03-18T23:59:59'
 */
const toEndOfDayOrNow = (dateString) => {
  const now = $dayjs()
  if (now.isSame(dateString, 'day')) {
    return now.format('YYYY-MM-DDTHH:mm:ss')
  }
  return $dayjs(dateString)
    .endOf('day')
    .format('YYYY-MM-DDTHH:mm:ss')
}

export {
  isMonthBeforeDay,
  sortDate,
  getTodaysDateString,
  getLocalDateString,
  toStartOfDay,
  toEndOfDayOrNow,
}
